import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { storesApi } from '../api/stores';
import { useAuth } from './AuthContext';
import type { Store } from '../types';

interface StoreContextValue {
  stores: Store[];
  storesLoading: boolean;
  // The store currently in focus for store-scoped screens.
  // null means "All stores" (only meaningful for super admins on some screens).
  selectedStoreId: number | null;
  setSelectedStoreId: (id: number | null) => void;
  // Whether the user is allowed to pick a store (super admin) or is locked
  // to their own (store admin).
  canSwitchStore: boolean;
  currentStore: Store | undefined;
}

const StoreContext = createContext<StoreContextValue | undefined>(undefined);

export function StoreProvider({ children }: { children: ReactNode }) {
  const { user, isSuperAdmin } = useAuth();

  const { data: stores = [], isLoading: storesLoading } = useQuery({
    queryKey: ['stores'],
    queryFn: storesApi.list,
    enabled: !!user,
  });

  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(
    user?.storeId ?? null,
  );

  // Keep a store admin pinned to their own store; default a super admin to
  // the first store once the list loads (if nothing is selected yet).
  useEffect(() => {
    if (!isSuperAdmin && user?.storeId != null) {
      setSelectedStoreId(user.storeId);
    }
  }, [isSuperAdmin, user?.storeId]);

  useEffect(() => {
    if (
      isSuperAdmin &&
      selectedStoreId == null &&
      stores.length > 0
    ) {
      // Leave as null (= All stores) by default for super admin; the
      // dashboard uses the consolidated view. Individual pages can still
      // pick a specific store from the selector.
    }
  }, [isSuperAdmin, selectedStoreId, stores]);

  const value = useMemo<StoreContextValue>(() => {
    const currentStore = stores.find((s) => s.id === selectedStoreId);
    return {
      stores,
      storesLoading,
      selectedStoreId,
      setSelectedStoreId,
      canSwitchStore: isSuperAdmin,
      currentStore,
    };
  }, [stores, storesLoading, selectedStoreId, isSuperAdmin]);

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStores(): StoreContextValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStores must be used within a StoreProvider');
  return ctx;
}
